import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LayoutDashboard, Users, Calendar, Keyboard, LifeBuoy, Compass } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@components/ui/dialog'

interface HelpDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)
const modKey = isMac ? '⌘' : 'Ctrl'

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex min-w-[24px] items-center justify-center rounded border border-ml-outline-variant/40 bg-ml-surface-container-high px-1.5 py-0.5 text-[11px] font-mono font-medium text-ml-on-surface-variant">
      {children}
    </kbd>
  )
}

export function HelpDialog({ open, onOpenChange }: HelpDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const quickLinks = [
    { key: 'sidebar.agenda', path: '/agenda', icon: Calendar },
    { key: 'sidebar.patients', path: '/patients', icon: Users },
    { key: 'sidebar.appointments', path: '/appointments', icon: LayoutDashboard },
  ]

  const shortcuts = [
    { keys: [modKey, 'K'], label: t('help.shortcut.search') },
    { keys: ['?'], label: t('help.shortcut.help') },
    { keys: ['Esc'], label: t('help.shortcut.close') },
  ]

  const go = (path: string) => {
    onOpenChange(false)
    navigate(path)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-ml-primary" />
            {t('help.title')}
          </DialogTitle>
          <DialogDescription>{t('help.subtitle')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-1">
          {/* Quick navigation */}
          <section>
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ml-on-surface-variant">
              <Compass className="h-3.5 w-3.5" />
              {t('help.quick_links')}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {quickLinks.map(({ key, path, icon: Icon }) => (
                <button
                  key={path}
                  type="button"
                  onClick={() => go(path)}
                  className="flex items-center gap-2.5 rounded-lg border border-ml-outline-variant/30 px-3 py-2 text-left text-sm transition-colors hover:border-ml-primary/40 hover:bg-ml-primary/5"
                >
                  <Icon className="h-4 w-4 shrink-0 text-ml-primary" />
                  <span className="truncate">{t(key)}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Keyboard shortcuts */}
          <section>
            <h3 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ml-on-surface-variant">
              <Keyboard className="h-3.5 w-3.5" />
              {t('help.shortcuts')}
            </h3>
            <ul className="divide-y divide-ml-outline-variant/20">
              {shortcuts.map((s) => (
                <li key={s.label} className="flex items-center justify-between py-2 text-sm">
                  <span className="text-ml-on-surface-variant">{s.label}</span>
                  <span className="flex items-center gap-1">
                    {s.keys.map((k) => (
                      <Kbd key={k}>{k}</Kbd>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* Support */}
          <section className="rounded-lg bg-ml-surface-container-high px-3 py-2.5 text-sm text-ml-on-surface-variant">
            <span className="font-medium text-ml-on-surface">{t('help.support.title')}</span>{' '}
            {t('help.support.body')}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}
